import React, { useState, useEffect, useMemo } from 'react';
import { Box, Text, Flex, Icon } from '@qga/roo-ui/components';
import { Flag } from '../PropertyPage/PropertyAvailability/RoomTypes/RoomTypeList/Offer/components/primitives';
import TextButton from 'components/TextButton';
import type { Inclusion } from 'types/property';
import { INCLUSIONS_PREVIEW_AMOUNT } from 'config/constants';
import { isDescriptionDifferent } from './offerInclusions.helper';
import useCustomInclusions from 'hooks/optimizely/useCustomInclusions';

const generateUniqueId = () => {
  return Math.random().toString(36).substring(2, 9);
};

interface OfferInclusionsProps {
  inclusions?: Inclusion[];
  isToggleExpansion?: boolean;
  isExpandText?: boolean;
  viewAll?: boolean;
  onClick?: () => void;
}

const OfferInclusions: React.FC<OfferInclusionsProps> = ({
  inclusions = [],
  isToggleExpansion = false,
  isExpandText = false,
  viewAll = false,
  onClick,
}) => {
  const [showAll, setShowAll] = useState(viewAll);

  useEffect(() => {
    setShowAll(viewAll);
  }, [viewAll]);

  const inclusionsListId = useMemo(() => `inclusions-list-${generateUniqueId()}`, []);

  const { isCustomInclusions } = useCustomInclusions();

  const previewInclusions = inclusions.slice(0, INCLUSIONS_PREVIEW_AMOUNT);
  const hasPreview = inclusions.length > INCLUSIONS_PREVIEW_AMOUNT;
  const inclusionsToRender = showAll ? inclusions : previewInclusions;

  const remainingCount = inclusions.length - INCLUSIONS_PREVIEW_AMOUNT;

  return (
    <Box color="greys.charcoal" mb={2}>
      <Flex flexDirection="column" data-testid="inclusions-list" id={inclusionsListId}>
        {inclusionsToRender.map(({ icon, name, description }, i) => {
          const showDescription = isDescriptionDifferent(description, name) && isCustomInclusions;

          if (!name) return null;

          return (
            <Flag key={i} icon={icon}>
              <Flex flexDirection="column">
                <Text fontSize="16px" data-testid="inclusion-name">
                  {name}
                </Text>
                {showDescription && (
                  <Text fontSize="14px" color="greys.steel" data-testid="inclusion-description">
                    {description}
                  </Text>
                )}
              </Flex>
            </Flag>
          );
        })}
      </Flex>

      {isToggleExpansion && !showAll && hasPreview && (
        <TextButton onClick={onClick} fontSize="16px" mt="3" data-testid="toggle-inclusions-message">
          {`+ ${remainingCount} more ${remainingCount === 1 ? 'inclusion' : 'inclusions'}`}
        </TextButton>
      )}

      {isExpandText && hasPreview && (
        <TextButton
          onClick={() => setShowAll(!showAll)}
          aria-expanded={showAll}
          aria-controls="inclusions-list"
          data-testid="view-all-inclusions-message"
          textDecoration="none"
          mt={2}
        >
          <Text color="brand.primary">{showAll ? 'View less' : `View all ${inclusions.length} inclusions`}</Text>
          <Icon name={showAll ? 'expandLess' : 'expandMore'} ml={2} />
        </TextButton>
      )}
    </Box>
  );
};

export default React.memo(OfferInclusions);
